import React, { useState } from 'react';
import { Switch, Route, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { push as Push } from 'redux-first-history';
import { useDispatch, useSelector } from 'react-redux';

import { TabsPanel, Container, PrimaryButton } from '@dev/ee-components';
import { capitalizeString } from '@dev/ee-tools';
import { AccessLevel, ProductType } from '@dev/ee-api/ee-api-models';

import Channels from './Channels';
import Summary from './Summary';
import DeviceDetailsOverview from './DeviceDetails';
import { StatisticsPaths } from '@api/services/dictionaries/paths';
import { getAccountStore } from '@api/store/account/actions';
import LinkTracking from './Channels/SingleChannel/LinkTracking';
import UpIcon from './Channels/SingleChannel/upIcon.svg';

enum TabsValues {
    Summary,
    Channels,
    DeviceDetails,
    LinkTracking,
}

const urlBasedOnTabs = {
    [TabsValues.Summary]: StatisticsPaths.StatisticsOverview,
    [TabsValues.Channels]: StatisticsPaths.StatisticsChannels,
    [TabsValues.DeviceDetails]: StatisticsPaths.StatisticsDeviceDetails,
    [TabsValues.LinkTracking]: StatisticsPaths.AllLinkTracking,
};

const getInitialTab = (currentPath: string): TabsValues =>
    Object.entries(urlBasedOnTabs)
        .find(([, path]) => path === currentPath)
        ?.at(0) as unknown as TabsValues;

const mobilePadding = {
    left: 16,
    right: 16,
};

const StatisticsTabs = (): JSX.Element => {
    const { GeneralInfo } = useSelector(getAccountStore);
    const { path } = useRouteMatch();
    const [tabValue, setTabValue] = useState(+getInitialTab(path) || TabsValues.Summary);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [isExportButtonDisabled, setIsExportButtonDisabled] = useState(true);
    const [clickedExport, setClickedExport] = useState(false);

    const changeTab = (_, tabValue): void => {
        setTabValue(tabValue);

        dispatch(Push(urlBasedOnTabs[tabValue]));
    };

    const statisticsTabs = [
        { label: t('summary'), value: TabsValues.Summary, available: true },

        {
            label: t('channels'),
            value: TabsValues.Channels,
            available: GeneralInfo?.AccountAccessLevel.includes(AccessLevel.ViewChannels),
        },

        {
            label: capitalizeString(t('devicedetails')),
            value: TabsValues.DeviceDetails,
            available: GeneralInfo?.ProductType === ProductType.API,
        },
        {
            label: capitalizeString(t('linktracking')),
            value: TabsValues.LinkTracking,
            available: GeneralInfo?.AccountAccessLevel.includes(AccessLevel.ViewReports),
        },
    ].filter((tab) => tab.available);

    const handleDisableExport = (newValue: boolean) => {
        setIsExportButtonDisabled(newValue);
    };

    const handleClickButton = () => {
        setClickedExport((prev) => !prev);
    };

    return (
        <Container paddingMobile={mobilePadding}>
            <TabsPanel
                defaultUseTabsValues
                title={t('activity')}
                tabs={statisticsTabs}
                onChange={changeTab}
                value={tabValue}
                additionalHeaderContent={
                    tabValue === TabsValues.LinkTracking && (
                        <PrimaryButton
                            id="exportlinks"
                            onClick={handleClickButton}
                            disabled={isExportButtonDisabled}
                            size="medium"
                            text={t('export')}
                            variant="text"
                            startIcon={<UpIcon />}
                        />
                    )
                }
            />
            <div>
                <Switch>
                    <Route path={StatisticsPaths.StatisticsOverview} exact component={Summary} />
                    <Route path={StatisticsPaths.StatisticsChannels} component={Channels} />
                    <Route path={StatisticsPaths.StatisticsDeviceDetails} component={DeviceDetailsOverview} />
                    <Route
                        path={StatisticsPaths.AllLinkTracking}
                        render={(props): JSX.Element => (
                            <LinkTracking
                                clickExport={clickedExport}
                                disabledExport={handleDisableExport}
                                {...props}
                                singleChannelPath={StatisticsPaths.AllLinkTrackingSingleLinkContacts}
                            />
                        )}
                    />
                </Switch>
            </div>
        </Container>
    );
};

export default StatisticsTabs;
